import { useParams } from "react-router-dom";
import { useMemo } from "react";
import { COURSES } from "@/data/courseSections";
import { ProgressDot } from "./ProgressDot";
import { useGetStudentProgress } from "@/api/classes";

interface ProgressIndicatorProps {
  courseId: string;
  workspaceId: string;
}

export const ProgressIndicator = ({
  courseId,
  workspaceId,
}: ProgressIndicatorProps) => {
  const { courseSlug, sectionId, lessonId } = useParams();

  const course = COURSES.find((course) => course.slug === courseSlug);
  const sectionIndex = course?.course.findIndex(
    (section) => section.slug === sectionId,
  );
  if (sectionIndex < 0) {
    return null;
  }
  const section = course?.course[sectionIndex];
  const lessonIndex = section?.lessons.findIndex(
    (lesson) => lesson.slug === lessonId,
  );
  if (lessonIndex < 0) {
    return null;
  }

  const progressQuery = useGetStudentProgress(courseId, workspaceId);

  const totalDots = 5;

  const progress = useMemo(() => {
    if (!progressQuery.isSuccess || progressQuery.data.length == 0) {
      return 0;
    }
    return progressQuery.data[0].lessons?.flat().reduce((acc, cur) => {
      if (cur.no_tests == 0) {
        return 20;
      }
      return acc + (cur.no_passed / cur.no_tests) * 20;
    }, 0);
  }, [progressQuery]);
  const activeDots = progress ? Math.round((progress / 100) * totalDots) : 0;

  return (
    <div className="flex items-center gap-3">
      {progressQuery.isSuccess && (
        <>
          <div className="flex space-x-1">
            {[...Array(totalDots)].map((_, index) => (
              <ProgressDot
                key={index}
                active={index < activeDots}
                progress={progress}
              />
            ))}
          </div>
          <span className="text-xs text-gray-500">{progress}%</span>
        </>
      )}
    </div>
  );
};
